import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { PartnerAccountStatus } from 'vopay';

/**
 * Raw API value stays the logic key (badge colour via `GetColorStatus`, `Blocked`/`Active` branches);
 * only the rendered string is localized (i18n-conventions rule). Falls back to the raw status string
 * (not the i18next key path) when a status isn't mapped, so an unrecognized value still renders
 * something readable instead of `Global.PartnerAccountStatus.Foo`.
 *
 * `PartnerAccountStatus` is the canonical set of statuses a response can carry on
 * `AccountBalance.AccountStatus` (the request-side filter vocabularies are subsets of it). The `Record`
 * below is exhaustive over that union, so tsc fails if a member is dropped; a status added to the
 * backend before the union catches up still renders (via the raw-string fallback), just untranslated
 * until it's added here.
 *
 * Memoized on `t`, whose identity changes only when the language does.
 */
export default function usePartnerAccountStatusLabel() {
  const { t } = useTranslation('translations', { keyPrefix: 'Global.PartnerAccountStatus' });

  return useMemo(() => {
    const labelsByStatus: Record<PartnerAccountStatus, string> = {
      'Pre-Application Check': t('Pre-Application Check', { defaultValue: 'Pre-Application Check' }),
      'Account Review and Validation': t('Account Review and Validation', {
        defaultValue: 'Account Review and Validation',
      }),
      'Sales Manager Approval': t('Sales Manager Approval', { defaultValue: 'Sales Manager Approval' }),
      'Agreement Pending': t('Agreement Pending', { defaultValue: 'Agreement Pending' }),
      'Application Pending': t('Application Pending', { defaultValue: 'Application Pending' }),
      'Setup Fee Pending': t('Setup Fee Pending', { defaultValue: 'Setup Fee Pending' }),
      Underwriting: t('Underwriting', { defaultValue: 'Underwriting' }),
      'Credit Risk Review': t('Credit Risk Review', { defaultValue: 'Credit Risk Review' }),
      'Account Provisioning': t('Account Provisioning', { defaultValue: 'Account Provisioning' }),
      'Certification Pending': t('Certification Pending', { defaultValue: 'Certification Pending' }),
      Active: t('Active', { defaultValue: 'Active' }),
      Disabled: t('Disabled', { defaultValue: 'Disabled' }),
      Rejected: t('Rejected', { defaultValue: 'Rejected' }),
      'Implementation Fee Pending': t('Implementation Fee Pending', {
        defaultValue: 'Implementation Fee Pending',
      }),
      'Document Review': t('Document Review', { defaultValue: 'Document Review' }),
      'Manager Approval': t('Manager Approval', { defaultValue: 'Manager Approval' }),
      Blocked: t('Blocked', { defaultValue: 'Blocked' }),
    };

    return {
      /** Falls back to the raw status string if it isn't (yet) mapped. */
      resolveStatusLabel: (rawStatus: PartnerAccountStatus): string => labelsByStatus[rawStatus] ?? rawStatus,
    };
  }, [t]);
}
